// src/components/insightsDiscovery/reducers/insightsDiscoveryReducer.js
import axios from "axios";

// ---------------------------------------------------------------------------
// Action Types
// ---------------------------------------------------------------------------
const SET_RUNS_BEGIN = "ID_SET_RUNS_BEGIN";
const SET_RUNS = "ID_SET_RUNS";
const SET_RUNS_ERROR = "ID_SET_RUNS_ERROR";

const SET_ACTIVE_RUN = "ID_SET_ACTIVE_RUN";

const SET_ANNOTATIONS_BEGIN = "ID_SET_ANNOTATIONS_BEGIN";
const SET_ANNOTATIONS = "ID_SET_ANNOTATIONS";

const SET_ACTIVE_ANNOTATION = "ID_SET_ACTIVE_ANNOTATION";

const EXECUTE_ANNOTATION_BEGIN = "ID_EXECUTE_ANNOTATION_BEGIN";
const EXECUTE_ANNOTATION_SUCCESS = "ID_EXECUTE_ANNOTATION_SUCCESS";
const EXECUTE_ANNOTATION_ERROR = "ID_EXECUTE_ANNOTATION_ERROR";

const SET_SYNTHESIS_BEGIN = "ID_SET_SYNTHESIS_BEGIN";
const SET_SYNTHESIS = "ID_SET_SYNTHESIS";

const SET_MODELS = "ID_SET_MODELS";
const SET_SELECTED_MODEL = "ID_SET_SELECTED_MODEL";

const SET_RUN_PREPEND = "ID_SET_RUN_PREPEND";

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------
const INITIAL_STATE = {
  runs: [],
  runsLoading: false,
  runsLoaded: false,
  runsTotal: 0,

  activeRun: null,

  annotations: [],
  analystConfig: null,
  annotationsLoading: false,
  annotationsLoaded: false,

  activeAnnotation: null,

  queryResult: null,
  queryLoading: false,
  queryError: null,

  synthesis: null,
  synthesisLoading: false,

  models: [],          // [{ key, label }] — derived from run history
  selectedModel: null, // key string or null (all models)
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
export default function insightsDiscoveryReducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case SET_RUNS_BEGIN:
      return { ...state, runsLoading: true, runsLoaded: false };
    case SET_RUNS:
      return {
        ...state,
        runsLoading: false,
        runsLoaded: true,
        runs: action.payload,
        runsTotal: action.total,
      };
    case SET_RUNS_ERROR:
      return { ...state, runsLoading: false, runsLoaded: false };

    case SET_ACTIVE_RUN:
      return {
        ...state,
        activeRun: action.payload,
        annotations: [],
        annotationsLoaded: false,
        activeAnnotation: null,
        queryResult: null,
        synthesis: null,
      };

    case SET_ANNOTATIONS_BEGIN:
      return { ...state, annotationsLoading: true, annotationsLoaded: false };
    case SET_ANNOTATIONS:
      return {
        ...state,
        annotationsLoading: false,
        annotationsLoaded: true,
        annotations: action.payload,
        analystConfig: action.analystConfig,
      };

    case SET_ACTIVE_ANNOTATION:
      return {
        ...state,
        activeAnnotation: action.payload,
        queryResult: null,
        queryError: null,
      };

    case EXECUTE_ANNOTATION_BEGIN:
      return { ...state, queryLoading: true, queryResult: null, queryError: null };
    case EXECUTE_ANNOTATION_SUCCESS:
      return { ...state, queryLoading: false, queryResult: action.payload };
    case EXECUTE_ANNOTATION_ERROR:
      return { ...state, queryLoading: false, queryError: action.error };

    case SET_SYNTHESIS_BEGIN:
      return { ...state, synthesisLoading: true, synthesis: null };
    case SET_SYNTHESIS:
      return { ...state, synthesisLoading: false, synthesis: action.payload };

    case SET_RUN_PREPEND: {
      // Add a single run to the front of the list if it isn't already there
      const alreadyPresent = state.runs.some((r) => r._id === action.payload._id);
      return {
        ...state,
        runs: alreadyPresent ? state.runs : [action.payload, ...state.runs],
      };
    }

    case SET_MODELS:
      return { ...state, models: action.payload };

    case SET_SELECTED_MODEL:
      return {
        ...state,
        selectedModel: action.payload,
        runs: [],
        runsLoaded: false,
        activeRun: null,
        annotations: [],
        activeAnnotation: null,
        queryResult: null,
        synthesis: null,
      };

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Action Creators
// ---------------------------------------------------------------------------

const headers = () => ({
  ClientToken: localStorage.getItem("clientToken"),
});

const SERVER = process.env.REACT_APP_BIGHORN_SERVER;

export const getRuns = (page = 1, modelKey = null) => (dispatch) => {
  dispatch({ type: SET_RUNS_BEGIN });
  axios
    .post(
      `${SERVER}/api/insightsDiscovery/getRuns`,
      { page, limit: 20, ...(modelKey ? { modelKey } : {}) },
      { headers: headers() },
    )
    .then((res) => {
      dispatch({ type: SET_RUNS, payload: res.data.runs, total: res.data.total });
    })
    .catch((err) => {
      console.error("[insightsDiscovery] getRuns failed:", err.message);
      dispatch({ type: SET_RUNS_ERROR });
    });
};

export const getModels = () => (dispatch) => {
  axios
    .get(`${SERVER}/api/insightsDiscovery/getModels`, { headers: headers() })
    .then((res) => {
      dispatch({ type: SET_MODELS, payload: res.data.models || [] });
    })
    .catch((err) => {
      console.error("[insightsDiscovery] getModels failed:", err.message);
    });
};

export const setSelectedModel = (modelKey) => (dispatch, getState) => {
  dispatch({ type: SET_SELECTED_MODEL, payload: modelKey });
  dispatch(getRuns(1, modelKey));
};

export const setActiveRun = (run) => (dispatch) => {
  dispatch({ type: SET_ACTIVE_RUN, payload: run });
  if (run) {
    dispatch(getAnnotations(run._id));
    dispatch(getSynthesis(run._id));
  }
};

export const getAnnotations = (runId) => (dispatch) => {
  dispatch({ type: SET_ANNOTATIONS_BEGIN });
  axios
    .post(
      `${SERVER}/api/insightsDiscovery/getAnnotations`,
      { runId },
      { headers: headers() },
    )
    .then((res) => {
      dispatch({
        type: SET_ANNOTATIONS,
        payload: res.data.annotations,
        analystConfig: res.data.analystConfig,
      });
    })
    .catch((err) => {
      console.error("[insightsDiscovery] getAnnotations failed:", err.message);
      dispatch({ type: SET_ANNOTATIONS, payload: [], analystConfig: null });
    });
};

export const setActiveAnnotation = (annotation) => (dispatch) => {
  dispatch({ type: SET_ACTIVE_ANNOTATION, payload: annotation });
};

export const executeAnnotation = (runId, question, annotationOrder) => (dispatch) => {
  dispatch({ type: EXECUTE_ANNOTATION_BEGIN });
  axios
    .post(
      `${SERVER}/api/insightsDiscovery/executeAnnotation`,
      { runId, question, annotationOrder },
      { headers: headers(), timeout: 120000 },
    )
    .then((res) => {
      dispatch({ type: EXECUTE_ANNOTATION_SUCCESS, payload: res.data });
    })
    .catch((err) => {
      const detail =
        err.response?.data?.error || err.response?.data?.detail || err.message;
      console.error("[insightsDiscovery] executeAnnotation failed:", detail);
      dispatch({ type: EXECUTE_ANNOTATION_ERROR, error: detail });
    });
};

/**
 * Fetch a single run by ID from the server.
 * Used when a shared URL points to a run not in the currently-loaded page.
 * Prepends it to the run list so _selectRunFromUrl can find it, then activates it.
 */
export const getRunById = (runId) => (dispatch) => {
  axios
    .get(`${SERVER}/api/insightsDiscovery/getRun`, {
      params: { runId },
      headers: headers(),
    })
    .then((res) => {
      const run = res.data.run;
      if (run) {
        dispatch({ type: SET_RUN_PREPEND, payload: run });
        dispatch(setActiveRun(run));
      }
    })
    .catch((err) => {
      console.error("[insightsDiscovery] getRunById failed");
    });
};

export const getSynthesis = (runId) => (dispatch) => {
  dispatch({ type: SET_SYNTHESIS_BEGIN });
  axios
    .post(
      `${SERVER}/api/insightsDiscovery/getSynthesis`,
      { runId },
      { headers: headers() },
    )
    .then((res) => {
      dispatch({ type: SET_SYNTHESIS, payload: res.data.synthesis });
    })
    .catch((err) => {
      console.error("[insightsDiscovery] getSynthesis failed:", err.message);
      dispatch({ type: SET_SYNTHESIS, payload: null });
    });
};
